import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { style } from './dropdown.style.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'


initializeIconsByRole({
  'cse-dropdown.arrow': { name: 'angle-down', style: 'light' },
})

export const dropdownAlignments = ['left', 'right'] as const
export type dropdownAlignmentsType = typeof dropdownAlignments

export const dropdownMenuWidths = ['anchor', 'content'] as const
export type DropdownMenuWidths = typeof dropdownMenuWidths

@customElement('cse-dropdown')
export class CSEDropdown extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ type: Boolean, reflect: true })
  active = false

  @property({ type: String })
  alignment: dropdownAlignmentsType[number] = 'left'

  @property({ type: String })
  description = ''

  @property({ type: String })
  value = ''

  @property({ type: Boolean })
  disabled = false

  @property({ type: String, attribute: 'menu-width' })
  menuWidth: DropdownMenuWidths[number] = 'content'

  @property({ type: Boolean, reflect: true, attribute: 'full-width' })
  fullWidth = false


  private dropdownDistance = 5

  private addOpenListeners() {
    document.addEventListener('click', this.handleDocumentClick)
  }

  private removeOpenListeners() {
    document.removeEventListener('click', this.handleDocumentClick)
  }

  private toggleDropdown() {
    if (this.disabled) { return }
    this.active = !this.active
  }

  private handleDocumentClick = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) { this.toggleDropdown() }
  }

  private renderDefaultAnchor = () => html`
    <button class="default-anchor">
      <slot name="icon"></slot>
      ${this.description.length ? html`
        <span class="description font-body-s">
          ${this.description}
        </span>
      ` : nothing}
      ${this.value.length ? html`
        <span class="value font-body-s">
          ${this.value}
        </span>
      ` : nothing}
      <cse-icon
        icon-role="cse-dropdown.arrow"
        class="${classMap({ 'icon-open':this.active })}"
        size="sm"
      ></cse-icon>
    </button>
  `

  override render() {
    return html`
      <cse-popup
        class="cse-dropdown"
        ?active="${this.active}"
        position="${this.alignment === 'left' ? 'bottom-start' : 'bottom-end'}"
        distance="${this.dropdownDistance}"
        sync="${ifDefined(this.menuWidth === 'anchor' ? 'width' : null)}"
      >
        <slot
          name="anchor"
          slot="anchor"
          @click="${this.toggleDropdown}"
          class="${classMap({
            'anchor': true,
            'active': this.active,
            'disabled': this.disabled,
            [`align-${this.alignment}`]: true,
          })}"
        >
          ${this.renderDefaultAnchor()}
        </slot>

        <cse-dropdown-menu>
          <slot class="options" @cse-option-clicked="${this.toggleDropdown}"></slot>
        </cse-dropdown-menu>

      </cse-popup>
    `
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (changedProperties.has('active')) {
      if (this.active) {
        this.addOpenListeners()
      } else {
        this.removeOpenListeners()
      }
    }
  }
}


declare global {
  interface HTMLElementTagNameMap {
    'cse-dropdown': CSEDropdown
  }
}
